import { useEffect, useMemo, useState } from "react";
import { t } from "../../i18n";

const STOREFRONT_CONFIG_UPDATED_EVENT = "storefront-config-updated";
const STOREFRONT_CONFIG_UPDATED_AT_KEY = "ft_storefront_config_updated_at";

export default function StorefrontCarousel({ apiBaseUrl }) {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchCurrentX, setTouchCurrentX] = useState(null);

  const total = useMemo(() => slides.length, [slides]);
  const normalizeBannerPercent = (value, fallback) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return fallback;
    }
    return Math.max(4, Math.min(78, Math.round(parsed)));
  };

  const canLoadBannerImage = (imageUrl) =>
    new Promise((resolve) => {
      const normalizedUrl = String(imageUrl || "").trim();
      if (!normalizedUrl || normalizedUrl === "null" || normalizedUrl === "undefined") {
        resolve(false);
        return;
      }

      const image = new window.Image();
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = normalizedUrl;
    });

  useEffect(() => {
    let cancelled = false;

    async function loadStorefrontBanners() {
      if (!apiBaseUrl) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${apiBaseUrl}/storefront/config`, {
          cache: "no-store"
        });
        if (!response.ok) {
          throw new Error("Failed to load storefront config");
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        if (Array.isArray(data.banners) && data.banners.length) {
          const candidateSlides = data.banners
            .map((item, index) => ({
              id: String(item.id || `banner-${index + 1}`),
              title: String(item.title || ""),
              annotation: String(item.annotation || item.subtitle || ""),
              buttonText: String(item.buttonText || item.cta || ""),
              buttonLink: String(item.buttonLink || ""),
              secondaryButtonText: String(item.secondaryButtonText || item.secondaryCta || ""),
              secondaryButtonLink: String(item.secondaryButtonLink || ""),
              contentX: normalizeBannerPercent(item.contentX ?? item.textX, 12),
              contentY: normalizeBannerPercent(item.contentY ?? item.textY, 52),
              imageUrl: String(item.imageUrl || "").trim()
            }))
            .filter((item) => item.imageUrl);

          const imageAvailability = await Promise.all(
            candidateSlides.map((item) => canLoadBannerImage(item.imageUrl))
          );

          if (cancelled) {
            return;
          }

          setSlides(candidateSlides.filter((_item, index) => imageAvailability[index]));
          setActive(0);
          setLoading(false);
          return;
        }

        setSlides([]);
        setLoading(false);
      } catch (_err) {
        if (!cancelled) {
          setSlides([]);
          setLoading(false);
        }
      }
    }

    function handleStorefrontConfigUpdated() {
      loadStorefrontBanners();
    }

    function handleStorage(event) {
      if (event.key === STOREFRONT_CONFIG_UPDATED_AT_KEY) {
        loadStorefrontBanners();
      }
    }

    loadStorefrontBanners();
    window.addEventListener(STOREFRONT_CONFIG_UPDATED_EVENT, handleStorefrontConfigUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      cancelled = true;
      window.removeEventListener(STOREFRONT_CONFIG_UPDATED_EVENT, handleStorefrontConfigUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    if (paused || total <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % total);
    }, 4800);

    return () => window.clearInterval(timer);
  }, [paused, total]);

  useEffect(() => {
    if (active < total) {
      return;
    }
    setActive(0);
  }, [active, total]);

  function goPrev() {
    if (total <= 1) {
      return;
    }
    setActive((current) => (current - 1 + total) % total);
  }

  function goNext() {
    if (total <= 1) {
      return;
    }
    setActive((current) => (current + 1) % total);
  }

  function handleTouchStart(event) {
    const x = event.touches?.[0]?.clientX;
    if (Number.isFinite(x)) {
      setPaused(true);
      setTouchStartX(x);
      setTouchCurrentX(x);
    }
  }

  function handleTouchMove(event) {
    const x = event.touches?.[0]?.clientX;
    if (Number.isFinite(x)) {
      setTouchCurrentX(x);
    }
  }

  function handleTouchEnd() {
    if (!Number.isFinite(touchStartX) || !Number.isFinite(touchCurrentX)) {
      setPaused(false);
      setTouchStartX(null);
      setTouchCurrentX(null);
      return;
    }

    const delta = touchCurrentX - touchStartX;
    const threshold = 42;

    if (delta > threshold) {
      goPrev();
    } else if (delta < -threshold) {
      goNext();
    }

    setPaused(false);
    setTouchStartX(null);
    setTouchCurrentX(null);
  }

  return (
    <section className={`storefront-carousel${!total ? " is-empty" : ""}`} aria-label={t("carouselAriaLabel")}>
      {!total ? (
        <div className={`container storefront-carousel-empty${loading ? " is-loading" : ""}`}>
          {loading ? (
            <div className="storefront-carousel-skeleton" aria-hidden="true">
              <div className="storefront-carousel-skeleton-media" />
              <div className="storefront-carousel-skeleton-copy">
                <span className="storefront-carousel-skeleton-kicker" />
                <span className="storefront-carousel-skeleton-title short" />
                <span className="storefront-carousel-skeleton-title long" />
                <span className="storefront-carousel-skeleton-line" />
                <span className="storefront-carousel-skeleton-line short" />
                <div className="storefront-carousel-skeleton-actions">
                  <span className="storefront-carousel-skeleton-button" />
                  <span className="storefront-carousel-skeleton-button ghost" />
                </div>
              </div>
            </div>
          ) : null}
          <div className="storefront-carousel-empty-art" aria-hidden="true">
            <span className="storefront-carousel-empty-orb orb-a" />
            <span className="storefront-carousel-empty-orb orb-b" />
            <span className="storefront-carousel-empty-orb orb-c" />
            <div className="storefront-carousel-empty-stage">
              <span className="storefront-carousel-empty-line short" />
              <span className="storefront-carousel-empty-line medium" />
              <span className="storefront-carousel-empty-line long" />
              <div className="storefront-carousel-empty-pills">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className="storefront-carousel-shell"
        style={{ display: total ? "block" : "none" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div className="storefront-carousel-track" style={{ transform: `translateX(-${active * 100}%)` }}>
          {slides.map((slide, index) => (
            <article key={`${slide.id}-${index}`} className={`storefront-slide ${slide.id || "banner"}`}>
              <img
                src={slide.imageUrl}
                alt={slide.title || slide.annotation || `${t("carouselBannerAltPrefix")} ${index + 1}`}
                className="storefront-slide-image"
                loading="lazy"
              />
              <div className="storefront-slide-overlay" />
              <div
                className="storefront-slide-copy is-positioned"
                style={{
                  left: `${normalizeBannerPercent(slide.contentX, 12)}%`,
                  top: `${normalizeBannerPercent(slide.contentY, 52)}%`
                }}
              >
                {slide.title ? <h2>{slide.title}</h2> : null}
                {slide.annotation ? <p>{slide.annotation}</p> : null}
                {(slide.buttonText && slide.buttonLink) || (slide.secondaryButtonText && slide.secondaryButtonLink) ? (
                  <div className="storefront-slide-actions">
                    {slide.buttonText && slide.buttonLink ? (
                      <a href={slide.buttonLink} className="button storefront-slide-primary">
                        {slide.buttonText}
                      </a>
                    ) : null}
                    {slide.secondaryButtonText && slide.secondaryButtonLink ? (
                      <a href={slide.secondaryButtonLink} className="button ghost storefront-slide-secondary">
                        {slide.secondaryButtonText}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <button
          type="button"
          className="storefront-carousel-arrow prev"
          onClick={goPrev}
          aria-label={t("carouselPrevAria")}
        >
          ‹
        </button>
        <button
          type="button"
          className="storefront-carousel-arrow next"
          onClick={goNext}
          aria-label={t("carouselNextAria")}
        >
          ›
        </button>

        <div className="storefront-carousel-dots" role="tablist" aria-label={t("carouselDotsAria")}>
          {slides.map((slide, index) => (
            <button
              key={`${slide.id}-dot-${index}`}
              type="button"
              role="tab"
              aria-selected={active === index}
              className={active === index ? "active" : ""}
              onClick={() => setActive(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
